<template>
    <div>
        <div v-for="day in sortedDays" :key="day.id" class="itinerary-day">
            <div class="row">
                <div class="itinerary-day__title col-xs-12 col-md-4">
                    <day v-if="!canManageDays" :day="day"></day>

                    <editable-day
                        v-else
                        :day="day"
                        @changed="dayChanged"
                        @deleted="dayDeleted">
                    </editable-day>
                </div>

                <div class="itinerary-day__items col-xs-12 col-md-8">
                    <div v-for="item in day.itinerary_items" :key="item.id" class="itinerary-item-wrapper">
                        <editable-itinerary-item
                            :item="item"
                            @changed="itemChanged"
                            @deleted="itemDeleted">
                        </editable-itinerary-item>
                    </div>

                    <create-itinerary-item
                        :day-id="day.id"
                        @created="itemCreated">
                    </create-itinerary-item>
                </div>
            </div>
        </div>

        <create-day
            class="mt-3"
            :event-id="event.id"
            @created="day => dayCreated(day)">
        </create-day>
    </div>
</template>

<script>
    import EditableItineraryItem from '@/components/EditableItineraryItem.vue';
    import CreateItineraryItem from '@/components/CreateItineraryItem.vue';
    import Day from '@/components/Day.vue';
    import EditableDay from '@/components/EditableDay.vue';
    import CreateDay from '@/components/CreateDay.vue';
    import dayRepository from "@/repository/days";
    import itineraryRepository from "@/repository/itinerary";

    export default {
        props: {
            days: {
                default: () => []
            },

            event: {
                required: true
            },

            canManageDays: {
                default: true
            }
        },

        components: {
            EditableItineraryItem,
            CreateItineraryItem,
            Day,
            EditableDay,
            CreateDay
        },

        data() {
            return {
                errors: {}
            }
        },

        computed: {
            sortedDays(){
                return this.days.sort((a,b)=>{
                    let dateA = new Date(a.date), dateB = new Date(b.date);
                    return dateA - dateB;
                }).map(day=>{
                    if (day.itinerary_items) {
                        day.itinerary_items.sort((a,b)=>{
                            let dateA = new Date('1970/01/01 ' + a.time), dateB = new Date('1970/01/01 ' + b.time);
                            return dateA - dateB;
                        });
                    }
                    
                    return day;
                });
            }
        },

        methods: {
            dayCreated(day) {
                dayRepository.store(day)
                    .then(response => {
                        let new_day = response.data;
                        this.days.push(new_day);

                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Day created',
                            text: 'Day successfully created.'
                        });
                    })
                    .catch(error => {
                        if (error.response.status == 422) {
                            let e = error.response.data.errors;

                            let errors = Object.keys(e).map(key => {
                                return e[key].join("<br/>");
                            }).join("<br/>");

                            this.$notify({
                                group: 'main',
                                type: 'error',
                                title: 'Form Errors',
                                text: errors,
                                duration: 10000,
                                width: '500px'
                            });
                        }
                    });
            },

            dayChanged(day) {
                dayRepository.update(day)
                    .then(response => {
                        let updated_day = response.data;
                        let idx = this.days.findIndex(d => d.id === updated_day.id);
                        let day = this.days[idx];

                        this.days.splice(idx, 1, {...day, ...updated_day}); 
                        // we destructure existing day (with itinterary), and then overwrite with
                        // destructured updated_day, which has no itinerary - this is just to avoid
                        // overwriting day.itinerary_items

                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Day updated',
                            text: 'Day successfully updated.'
                        });
                    })
                    .catch(error => {
                        if (error.response.status == 422) {
                            let e = error.response.data.errors;

                            let errors = Object.keys(e).map(key => {
                                return e[key].join("<br/>");
                            }).join("<br/>");

                            this.$notify({
                                group: 'main',
                                type: 'error',
                                title: 'Form Errors',
                                text: errors,
                                duration: 10000,
                                width: '500px'
                            });
                        }
                    });
            },

            dayDeleted(day) {
                dayRepository.destroy(day.id)
                    .then(response => {
                        let idx = this.days.findIndex(d => d.id === day.id);
                        this.days.splice(idx, 1);

                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Day deleted',
                            text: 'Day successfully deleted.'
                        });
                    })
                    .catch(error => {
                        if (error.response.status == 422) {
                            let e = error.response.data.errors;

                            let errors = Object.keys(e).map(key => {
                                return e[key].join("<br/>");
                            }).join("<br/>");

                            this.$notify({
                                group: 'main',
                                type: 'error',
                                title: 'Form Errors',
                                text: errors,
                                duration: 10000,
                                width: '500px'
                            });
                        }
                    });
            },

            itemCreated(item) {
                itineraryRepository.store(item)
                    .then(response => {
                        let new_item = response.data;
                        let day_idx = this.days.findIndex(d => d.id == new_item.day_id);
                        let day = this.days[day_idx];
                        if (!("itinerary_items" in day)) this.$set(day, 'itinerary_items', []);
                        day.itinerary_items.push(new_item);

                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Itinerary item created',
                            text: 'Itinerary item successfully created.'
                        });
                    })
                    .catch(error => {
                        if (error.response.status == 422) {
                            let e = error.response.data.errors;

                            let errors = Object.keys(e).map(key => {
                                return e[key].join("<br/>");
                            }).join("<br/>");

                            this.$notify({
                                group: 'main',
                                type: 'error',
                                title: 'Form Errors',
                                text: errors,
                                duration: 10000,
                                width: '500px'
                            });
                        }
                    });
            },

            itemChanged(item) {
                itineraryRepository.update(item)
                    .then(response => {
                        let updated_item = response.data;
                        let day_idx = this.days.findIndex(d => d.id === updated_item.day_id);
                        let item_idx = this.days[day_idx].itinerary_items.findIndex(i => i.id === updated_item.id);
                        this.days[day_idx].itinerary_items.splice(item_idx, 1, updated_item);

                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Itinerary item edited',
                            text: 'Itinerary item successfully edited.'
                        });
                    })
                    .catch(error => {
                        if (error.response.status == 422) {
                            let e = error.response.data.errors;

                            let errors = Object.keys(e).map(key => {
                                return e[key].join("<br/>");
                            }).join("<br/>");

                            this.$notify({
                                group: 'main',
                                type: 'error',
                                title: 'Form Errors',
                                text: errors,
                                duration: 10000,
                                width: '500px'
                            });
                        }
                    });
            },
            
            itemDeleted(item) {
                itineraryRepository.destroy(item.id)
                    .then(response => {
                        let deleted_item = response.data;
                        let day_idx = this.days.findIndex(d => d.id === deleted_item.day_id);
                        let item_idx = this.days[day_idx].itinerary_items.findIndex(i => i.id === deleted_item.id);
                        this.days[day_idx].itinerary_items.splice(item_idx, 1);

                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Itinerary item deleted',
                            text: 'Itinerary item successfully deleted.'
                        });
                        
                    })
                    .catch(error => {
                        if (error.response.status == 422) {
                            let e = error.response.data.errors;

                            let errors = Object.keys(e).map(key => {
                                return e[key].join("<br/>");
                            }).join("<br/>");

                            this.$notify({
                                group: 'main',
                                type: 'error',
                                title: 'Form Errors',
                                text: errors,
                                duration: 10000,
                                width: '500px'
                            });
                        }
                    });
            }
        }
    }
</script>

<style lang="scss" scoped>
    .itinerary-item-wrapper {
        border-bottom: 1px solid $color-light-blue;
        margin-bottom: 1rem;
    }

    .itinerary-day__items {
        margin-bottom: 3rem;
    }

    .itinerary__create-day {
        margin-top: 1rem;
    }

    //.itinerary__day {
    //    // margin: 20px 0;
    //}
    //
    //.itinerary__create-day {
    //    margin-top: 20px;
    //}
    //
    //.itinerary__inner {
    //    padding-left: 15px;
    //    border-left: 4px solid $color-light-blue;
    //}

    
</style>